import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Search as SearchIcon, X } from 'lucide-react';
import { tmdbApi } from '../api/tmdb';
import MovieCard from './MovieCard';

const useDebouncedValue = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

const SearchModal = ({ isOpen, onClose, openDetail, controlledQuery, onQueryChange }) => {
  const [internalQuery, setInternalQuery] = useState(controlledQuery || '');

  useEffect(() => {
    if (typeof controlledQuery === 'string') setInternalQuery(controlledQuery);
  }, [controlledQuery]);

  const setQuery = (q) => {
    setInternalQuery(q);
    onQueryChange && onQueryChange(q);
  };

  const debounced = useDebouncedValue(internalQuery, 350);

  const { data, isFetching } = useQuery({
    queryKey: ['searchMulti', debounced],
    queryFn: () => tmdbApi.searchMulti(debounced, 1),
    enabled: !!debounced && debounced.trim().length > 0 && isOpen,
    staleTime: 1000 * 30,
  });

  const suggestions = useMemo(() => {
    const raw = data?.results || [];
    return raw
      .filter((r) => r.media_type === 'movie' || r.media_type === 'tv')
      .slice(0, 6)
      .map((r) => ({
        id: r.id,
        media_type: r.media_type,
        title: r.title || r.name || r.original_title || r.original_name,
      }));
  }, [data]);

  const handleOpenResult = (item) => {
    onClose();
    openDetail && openDetail(item, internalQuery);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120]"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-xl" onClick={onClose} />

          {/* Content */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="relative z-[125] mx-auto my-8 w-[95%] max-w-6xl min-h-[70vh] rounded-lg border border-white/15 bg-white/10 overflow-hidden"
          >
            {/* Header */}
            <div className="p-4 sm:p-6 border-b border-white/10 bg-gradient-to-br from-slate-900/60 to-slate-800/40">
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60" size={18} />
                  <input
                    autoFocus
                    value={internalQuery}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search for movies or TV shows..."
                    className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-slate-500"
                  />
                </div>
                <button onClick={onClose} className="p-3 rounded-lg bg-white/10  hover:bg-white/20 text-white">
                  <X size={18} />
                </button>
              </div>

              {/* Typeahead
              {internalQuery.trim() && suggestions.length > 0 && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {suggestions.map((s) => (
                    <div key={`${s.media_type}-${s.id}`} className="px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white/90 text-sm">
                      {s.title}
                      <span className="ml-2 text-white/60 text-xs">{s.media_type.toUpperCase()}</span>
                    </div>
                  ))}
                </div>
              )} */}
            </div>

            {/* Results */}
            <div className="p-4 sm:p-6 my-3 h-[70vh] overflow-y-auto">
              {!internalQuery.trim() && (
                <div className="text-center text-white/70 py-10">Start typing to search...</div>
              )}

              {isFetching && (
                <div className="text-center text-white/70 py-10">Searching...</div>
              )}

              {!isFetching && data?.results && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
                  {data.results
                    .filter((r) => r.media_type === 'movie' || r.media_type === 'tv')
                    .map((item) => (
                      <div key={`${item.media_type}-${item.id}`} onClick={() => handleOpenResult(item)}>
                        <MovieCard movie={item} variant="search" disableInlineModal onOpenModal={() => handleOpenResult(item)} />
                      </div>
                    ))}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SearchModal;
